import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * SD Cart Sync
 *
 * The theme renders the cart from its own mutations, announced through Shopify's standard
 * cart events. Anything else that writes to the cart — an app adding a gift with purchase,
 * the shopper in another tab — announces nothing the theme can rely on, so the rendered cart
 * drifts from the real one.
 *
 * This module re-reads the cart at the moments a foreign write is likely to have landed,
 * and when the page disagrees with what it reads, republishes the cart through the same
 * `shopify:cart:lines-update` event the theme's own mutations use. Every consumer (item
 * list, header count, quantity labels, promotion tracker) then converges by the code path
 * it already has. Nothing here is a dependency for correctness of the theme's own actions.
 *
 * Triggers, cheapest first:
 * - a third-party "I changed the cart" hint (Intelligems' undocumented `ig:CartUpdated`)
 * - the cart drawer opening
 * - the tab becoming visible again, or being restored from the back/forward cache
 * - the shopper leaving a cart quantity field after a check was skipped for it
 */

const SOURCE = 'sd-cart-sync';

/** Third-party "the cart changed" events dispatched on `window`. Hints only. */
const VENDOR_HINTS = ['ig:CartUpdated'];

/** Collapse bursts of triggers into one read. */
const SETTLE_MS = 300;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let timer;
let inFlight = false;
let skippedForFocusedInput = false;

function schedule() {
  clearTimeout(timer);
  timer = setTimeout(sync, SETTLE_MS);
}

/** A theme mutation is mid-flight, or the shopper is typing a quantity. Not a moment to repaint. */
function mutationPending() {
  if (document.querySelector('cart-items-component.cart-items-disabled')) return true;

  const active = document.activeElement;
  if (active instanceof HTMLInputElement && active.closest('cart-items-component')) {
    skippedForFocusedInput = true;
    return true;
  }

  return false;
}

/** @returns {Promise<any>} The Ajax cart, sharing the cart list's in-flight read when there is one. */
async function readCart() {
  /** @type {any} */
  const cartItems = document.querySelector('cart-items-component');
  if (typeof cartItems?.fetchCartData === 'function') return cartItems.fetchCartData();

  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Does the page already show this cart? Compares what the server rendered — line keys and
 * item counts — never a value the shopper may be editing.
 * @param {{ item_count: number, items: Array<{ key: string }> }} cart
 */
function pageMatches(cart) {
  const cartKeys = cart.items
    .map((item) => item.key)
    .sort()
    .join(',');

  for (const list of document.querySelectorAll('cart-items-component')) {
    const rows = Array.from(list.querySelectorAll('tr[data-key]:not(.removing)'));
    const keys = rows
      .map((row) => /** @type {HTMLElement} */ (row).dataset.key)
      .sort()
      .join(',');
    if (keys !== cartKeys) return false;

    const count = Number.parseInt(list.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
    if (!Number.isNaN(count) && count !== cart.item_count) return false;
  }

  for (const badge of document.querySelectorAll('cart-icon [ref="cartBubbleCount"], [data-cart-count]')) {
    // The header bubble renders nothing at 100+, so it cannot be compared there.
    if (cart.item_count >= 100 && badge.hasAttribute('ref')) continue;

    const shown = Number.parseInt(badge.textContent?.trim() || '0', 10) || 0;
    if (shown !== cart.item_count) return false;
  }

  return true;
}

/** @param {any} cart */
function publish(cart) {
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, items: cart.items, itemCount: cart.item_count, didError: false },
      }),
    })
  );
}

async function sync() {
  if (inFlight) return schedule();
  if (mutationPending()) return;

  inFlight = true;
  try {
    const cart = await readCart();

    // The shopper acted while the read was in the air; their action will render fresh.
    if (mutationPending()) return;
    if (pageMatches(cart)) return;

    publish(cart);
  } catch (error) {
    console.warn('[sd-cart-sync] Could not read the cart:', error);
  } finally {
    inFlight = false;
  }
}

for (const name of VENDOR_HINTS) window.addEventListener(name, schedule);

// `dialog:open` does not bubble; capture on document also survives header re-renders.
document.addEventListener(
  'dialog:open',
  (event) => {
    if (event.target instanceof Element && event.target.matches('cart-drawer-component')) schedule();
  },
  { capture: true }
);

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') schedule();
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) schedule();
});

document.addEventListener('focusout', (event) => {
  if (!skippedForFocusedInput) return;
  if (event.target instanceof HTMLInputElement && event.target.closest('cart-items-component')) {
    skippedForFocusedInput = false;
    schedule();
  }
});
